import { Typography, useBreakpoint } from "@avenue-8/sidekick-ui";
import styled from "@emotion/styled";
import { Dialog, DialogContent, DialogTitle, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { BottomSheet } from "@shared/components/BottomSheet";
import type { DialogProps } from "@mui/material";
import type { ReactNode } from "react";

const StyledDialog = styled(Dialog)`
  & .MuiDialog-paper {
    border-radius: 0.5rem;
    min-width: 22rem;
  }

  & .MuiDialog-paperFullScreen {
    border-radius: 0;
  }
`;

const FullScreenDialogContent = styled(DialogContent)`
  display: flex;
  flex-direction: column;
`;

const SheetContent = styled.div`
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

// Padding/negative-margin give focused inputs' outline room before this element's own overflow-y: auto clips it.
const SheetMain = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0 2px;
  margin: 0 -2px;
`;

type Props = {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  maxWidth?: DialogProps["maxWidth"];
  onExited?: () => void;
  // For a dialog that can't be dismissed yet: hides the desktop close icon, and on mobile
  // drops swipe-to-close, so there's no control or gesture that looks like it works but
  // silently doesn't.
  disableClose?: boolean;
  fullScreen?: boolean;
};

export const ResponsiveSheetDialog = (props: Props) => {
  const {
    open,
    onClose,
    title,
    children,
    maxWidth = "xs",
    onExited,
    disableClose = false,
    fullScreen = false,
  } = props;
  const isLaptopDown = useBreakpoint("laptop");

  if (isLaptopDown) {
    return (
      <BottomSheet
        open={open}
        onClose={onClose}
        onExited={onExited}
        disableClose={disableClose}
        fullScreen={fullScreen}
      >
        <SheetContent>
          {title && <Typography variant="h6">{title}</Typography>}
          <SheetMain>{children}</SheetMain>
        </SheetContent>
      </BottomSheet>
    );
  }

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth
      fullScreen={fullScreen}
      TransitionProps={{ onExited }}
    >
      {title && (
        <DialogTitle>
          {title}
          {!disableClose && (
            <IconButton
              size="small"
              onClick={onClose}
              aria-label="Close"
              sx={{ position: "absolute", right: 16, top: 16 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </DialogTitle>
      )}
      {fullScreen ? (
        <FullScreenDialogContent>{children}</FullScreenDialogContent>
      ) : (
        <DialogContent>{children}</DialogContent>
      )}
    </StyledDialog>
  );
};
